import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Divider from '@mui/material/Divider';

import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

import * as ETVConstants from '../ETVConstants';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

class MessageDialog extends React.Component {
  getTone(type) {
    switch (type) {
      case 'error':
        return { key: 'error', icon: <ErrorIcon sx={{ fontSize: 22 }} />, label: ETVConstants.trans('error') || 'error' };
      case 'alert':
      case 'warning':
        return { key: 'warning', icon: <WarningAmberIcon sx={{ fontSize: 22 }} />, label: ETVConstants.trans('warning') || 'warning' };
      case 'success':
        return { key: 'success', icon: <CheckCircleIcon sx={{ fontSize: 22 }} />, label: ETVConstants.trans('success') || 'success' };
      case 'info':
      default:
        return { key: 'info', icon: <InfoIcon sx={{ fontSize: 22 }} />, label: ETVConstants.trans('info') || 'info' };
    }
  }

  render() {
    const { open, onClose, type, title, message, spinning } = this.props;
    const tone = this.getTone(type);

    const toneBg = (t) => {
      const map = {
        info: t.palette.info.main,
        success: t.palette.success.main,
        warning: t.palette.warning.main,
        error: t.palette.error.main,
      };
      return map[tone.key] || t.palette.info.main;
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        TransitionComponent={Transition}
        keepMounted
        aria-labelledby="message-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 22px 70px rgba(0,0,0,0.25)',
          },
        }}
      >
        {/* Top colored strip */}
        <Box
          sx={{
            height: 6,
            bgcolor: toneBg,
          }}
        />

        {/* App-like header row */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: (t) => `${toneBg(t)}22`, // light tint
              color: toneBg,
              flex: '0 0 auto',
            }}
          >
            {tone.icon}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="message-dialog-title"
              variant="subtitle1"
              sx={{ fontWeight: 800, lineHeight: 1.2 }}
              noWrap
            >
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {tone.label}
            </Typography>
          </Box>

          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <DialogContent sx={{ p: 2 }}>
          {/* Message card */}
          <Box
            sx={{
              borderRadius: 2.5,
              p: 1.75,
              bgcolor: (t) => t.palette.background.default,
              border: '1px solid',
              borderColor: (t) => t.palette.divider,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.65,
              }}
            >
              {message}
            </Typography>
          </Box>

          {/* Loading row (app-like) */}
          {spinning && (
            <Box
              sx={{
                mt: 2,
                borderRadius: 2,
                p: 1.25,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                bgcolor: (t) => t.palette.action.hover,
              }}
            >
              <CircularProgress size={18} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {ETVConstants.trans('working') || 'working'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ETVConstants.trans('please_wait') || 'please wait…'}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={onClose} variant="outlined">
            {ETVConstants.trans('close')}
          </Button>

          {/* Optional: primary action button if you ever want it later */}
          {/* <Button onClick={onClose} variant="contained" disableElevation>ok</Button> */}
        </DialogActions>
      </Dialog>
    );
  }
}

export default MessageDialog;
